import { apiRequest } from './client'
import { demoUiDelay, isDemoUiEnabled } from '../dev/demoMode'

export type CatalogListDto = {
  id: number
  name: string
  systemKey: string | null
}

type Raw = Record<string, unknown>

let demoLists: CatalogListDto[] = [
  { id: 1, name: 'Избранное', systemKey: 'favorites' },
  { id: 2, name: 'Постоянный заказ', systemKey: null },
]
let nextDemoListId = 3
const demoMemberships = new Map<number, Set<number>>([
  [1001, new Set([1, 2])],
  [1004, new Set([1])],
  // Динамика цен: отдельные тестовые позиции, чтобы фильтры списков можно было проверить локально.
  [101, new Set([1, 2])],
  [204, new Set([2])],
  [310, new Set([1])],
  [118, new Set([2])],
])

const normalize = (raw: Raw): CatalogListDto => ({
  id: Number(raw.id ?? 0),
  name: String(raw.name ?? ''),
  systemKey: (raw.systemKey as string | null | undefined) ?? (raw.system_key as string | null | undefined) ?? null,
})

/** Только для локального демо: таблица ведёт себя как при работе с сервером. */
export const demoCatalogListIdsForItem = (itemId: number): number[] =>
  [...(demoMemberships.get(itemId) ?? new Set<number>())]

export const listCatalogLists = async (organizationId: number): Promise<CatalogListDto[]> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return demoLists.map((list) => ({ ...list }))
  }
  const params = new URLSearchParams({ organization_id: String(organizationId) })
  const response = await apiRequest<Raw[]>(`/buyer/catalog/lists?${params.toString()}`)
  return response.map(normalize)
}

export const createCatalogList = async (organizationId: number, name: string): Promise<CatalogListDto> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    const created = { id: nextDemoListId++, name: name.trim(), systemKey: null }
    demoLists = [...demoLists, created]
    return created
  }
  return normalize(
    await apiRequest<Raw>('/buyer/catalog/lists', {
      method: 'POST',
      body: { organization_id: organizationId, name },
    }),
  )
}

export const renameCatalogList = async (
  organizationId: number,
  listId: number,
  name: string,
): Promise<CatalogListDto> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    const current = demoLists.find((list) => list.id === listId)
    if (!current) throw new Error('Список не найден')
    if (current.systemKey) throw new Error('Системный список нельзя переименовать')
    const normalizedName = name.trim().replace(/\s+/g, ' ')
    if (demoLists.some((list) => list.id !== listId && list.name.toLocaleLowerCase() === normalizedName.toLocaleLowerCase())) {
      throw new Error('Список с таким названием уже есть')
    }
    demoLists = demoLists.map((list) => (list.id === listId ? { ...list, name: normalizedName } : list))
    return { ...demoLists.find((list) => list.id === listId)! }
  }
  return normalize(
    await apiRequest<Raw>(listUrl(organizationId, listId), {
      method: 'PUT',
      body: { organization_id: organizationId, name },
    }),
  )
}

const listItemUrl = (organizationId: number, listId: number, itemId: number): string => {
  const params = new URLSearchParams({ organization_id: String(organizationId) })
  return `/buyer/catalog/lists/${listId}/items/${itemId}?${params.toString()}`
}

const listUrl = (organizationId: number, listId: number): string => {
  const params = new URLSearchParams({ organization_id: String(organizationId) })
  return `/buyer/catalog/lists/${listId}?${params.toString()}`
}

export const addCatalogListItem = async (organizationId: number, listId: number, itemId: number): Promise<void> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    const memberships = demoMemberships.get(itemId) ?? new Set<number>()
    memberships.add(listId)
    demoMemberships.set(itemId, memberships)
    return
  }
  await apiRequest<void>(listItemUrl(organizationId, listId, itemId), { method: 'PUT' })
}

export const addCatalogListItems = async (
  organizationId: number,
  listId: number,
  itemIds: number[],
): Promise<number> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    let addedCount = 0
    for (const itemId of new Set(itemIds)) {
      const memberships = demoMemberships.get(itemId) ?? new Set<number>()
      if (!memberships.has(listId)) addedCount += 1
      memberships.add(listId)
      demoMemberships.set(itemId, memberships)
    }
    return addedCount
  }
  const uniqueItemIds = [...new Set(itemIds)]
  let addedCount = 0
  for (let offset = 0; offset < uniqueItemIds.length; offset += 500) {
    const response = await apiRequest<{ added_count?: unknown }>(`/buyer/catalog/lists/${listId}/items`, {
      method: 'PUT',
      body: { organization_id: organizationId, item_ids: uniqueItemIds.slice(offset, offset + 500) },
    })
    addedCount += Number(response.added_count ?? 0)
  }
  return addedCount
}

export const removeCatalogListItem = async (organizationId: number, listId: number, itemId: number): Promise<void> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    demoMemberships.get(itemId)?.delete(listId)
    return
  }
  await apiRequest<void>(listItemUrl(organizationId, listId, itemId), { method: 'DELETE' })
}

export const deleteCatalogList = async (organizationId: number, listId: number): Promise<void> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    const list = demoLists.find((candidate) => candidate.id === listId)
    if (list?.systemKey) {
      throw new Error('Системный список нельзя удалить')
    }
    demoLists = demoLists.filter((candidate) => candidate.id !== listId)
    for (const memberships of demoMemberships.values()) {
      memberships.delete(listId)
    }
    return
  }
  await apiRequest<void>(listUrl(organizationId, listId), { method: 'DELETE' })
}
